import { Injectable } from '@angular/core';
import {
    ApiDataStatus,
    EducationalFacilityDTO,
    MeasureDTO,
    SchoolAtlasService,
    fallbackIfDelayed, type GetAllFacilitiesRequestParams,
} from '@ui/shared/util-api';
import { BehaviorSubject, Observable, catchError, map, of, switchMap, tap, zip } from 'rxjs';
import { SchoolMapItem } from '../../definitions/school-map-item';
import { Result, ToResult, pipeDataResult, toDataResult } from '@team23/ts-result';
import { SchoolMapFilterConfigService } from '../school-map-filter-config';
import { StudentGuideMapItem } from '../../definitions/student-guide-map-item';

type MapDataResult = Result<Array<SchoolMapItem>, ApiDataStatus>;

@Injectable({ providedIn: 'root' })
export class SchoolMapMapDataService {

    private readonly _reloadMapItems$ = new BehaviorSubject<null>(null);

    constructor(
        private readonly _schoolMapApi: SchoolAtlasService,
        private readonly _filterConfigService: SchoolMapFilterConfigService,
    ) { }

    public getMapItems$(
        params: {
            titleContains?: string | number;
            typeIds?: Array<number>;
            educationalBranchIds?: Array<number>;
            careServiceIds?: Array<number>;
            showStudentGuides?: boolean;
            measureTypeIds?: Array<number>;
            measureStatuses?: Array<string>;
        },
    ): Observable<MapDataResult> {
        const requestParams: GetAllFacilitiesRequestParams = {};

        if (params.titleContains) {
            requestParams.search = params.titleContains.toString();
        }

        if (params.typeIds?.length) {
            requestParams.typeIds = params.typeIds;
        }

        if (params.educationalBranchIds?.length) {
            requestParams.educationalBranchIds = params.educationalBranchIds;
        }

        if (params.careServiceIds?.length) {
            requestParams.educationalCareServiceIds = params.careServiceIds;
        }

        return this._reloadMapItems$
            .pipe(
                switchMap(() =>
                    fallbackIfDelayed(
                        zip([
                            this._schoolMapApi.getAllFacilities(requestParams),
                            this._schoolMapApi.getAllMeasures({}),
                            params.showStudentGuides
                                ? this._schoolMapApi.getAllGuides({})
                                : of(undefined),
                        ])
                            .pipe(
                                map(([facilitiesResults, measuresResults, studentGuidesResults]) => ({
                                    facilities: facilitiesResults.data,
                                    measures: measuresResults.data,
                                    studentGuides: studentGuidesResults?.data ?? [],
                                })),
                                toDataResult(),
                                // Catch the error within the switchmap to keep the outer observable from closing.
                                catchError(() => of(ToResult.fromErrorKey(ApiDataStatus.GenericError))),
                            ),
                        ToResult.fromErrorKey(ApiDataStatus.Loading),
                    ),
                ),
                pipeDataResult(
                    tap(({ data: { facilities } }) => {
                        this._filterConfigService.updateMapItems(facilities);
                    }),
                    map(({ data: { facilities, measures, studentGuides } }) =>
                        new Array<SchoolMapItem>()
                            .concat(
                                this._mapFacilitiesWithMeasures(facilities, measures, params),
                                studentGuides.map(guide => new StudentGuideMapItem(guide)),
                            )
                            .sort((a, b) =>
                                (a.title === b.title
                                    ? 0
                                    : a.title > b.title
                                        ? 1
                                        : -1),
                            ),
                    ),
                    toDataResult(),
                ),
            );
    }

    public reloadMapItems(): void {
        this._reloadMapItems$.next(null);
    }

    private _mapFacilitiesWithMeasures(
        facilities: Array<EducationalFacilityDTO>,
        measures: Array<MeasureDTO>,
        params: {
            measureTypeIds?: Array<number>;
            measureStatuses?: Array<string>;
        },
    ): Array<SchoolMapItem> {
        return facilities
            .map(facility =>
                new SchoolMapItem(
                    facility,
                    {
                        measures:
                            measures
                                .filter(measure => measure.facility === facility.id)
                                .sort((previous, current) => previous.status - current.status),
                    },
                ),
            )
            // If measure types or statuses are filtered, only keep the matching facilities.
            .filter(facility =>
                ((params.measureTypeIds?.length || params.measureStatuses?.length)
                    ? facility.measureGroups.some(measureGroup =>
                        measureGroup.measures.some(measure =>
                            (
                                params.measureTypeIds?.length
                                    ? params.measureTypeIds.includes(measure.type)
                                    : true
                            ) && (
                                params.measureStatuses?.length
                                    ? params.measureStatuses.includes(measure.status)
                                    : true
                            ),
                        ),
                    )
                    : true),
            );
    }
}
